import { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Briefcase,
  Building2,
  Car,
  ChevronLeft,
  ChevronRight,
  Coins,
  Cpu,
  Handshake,
  Landmark,
  Repeat,
  Shield,
  Smartphone,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import {
  DIFFERENTIATORS,
  ENTERPRISE,
  INDUSTRIES,
  INTEGRATIONS,
  JOURNEY,
} from "../../constants/collect";
import { CornerMarks, SectionShell, useFade } from "./ui";

const INDUSTRY_ICONS = {
  landmark: Landmark,
  building: Building2,
  wallet: Wallet,
  coins: Coins,
  users: Users,
  shield: Shield,
  smartphone: Smartphone,
  cpu: Cpu,
  handshake: Handshake,
  car: Car,
  book: BookOpen,
  zap: Zap,
  repeat: Repeat,
  briefcase: Briefcase,
};

const JOURNEY_PHASES = [
  {
    id: "onboard",
    n: "01",
    title: "Onboard the book",
    copy: "Portfolios land, validate and become recovery cases.",
    steps: JOURNEY.slice(0, 4),
  },
  {
    id: "work",
    n: "02",
    title: "Work the case",
    copy: "Segment, engage, then intervene with care.",
    steps: JOURNEY.slice(4, 8),
  },
  {
    id: "settle",
    n: "03",
    title: "Settle and close",
    copy: "Pay, reconcile, escalate, report, then close.",
    steps: JOURNEY.slice(8, 12),
  },
];

function JourneyBoard() {
  const [active, setActive] = useState(0);
  const step = JOURNEY[active];
  const last = JOURNEY.length - 1;
  const go = (index) => setActive(Math.max(0, Math.min(last, index)));

  const onTrackKeyDown = (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      go(active + 1);
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      go(active - 1);
    }
    if (event.key === "Home") {
      event.preventDefault();
      go(0);
    }
    if (event.key === "End") {
      event.preventDefault();
      go(last);
    }
  };

  return (
    <div className="mt-14">
      <div
        className="overflow-hidden border border-navy/10"
        role="listbox"
        aria-label="Recovery workflow stations"
        tabIndex={0}
        onKeyDown={onTrackKeyDown}
      >
        {JOURNEY_PHASES.map((phase) => (
          <div
            key={phase.id}
            className="grid border-t border-navy/10 first:border-t-0 sm:grid-cols-2 lg:grid-cols-[10rem_repeat(4,minmax(0,1fr))]"
          >
            <div className="flex items-center gap-3 bg-collect-red px-4 py-4 text-white sm:col-span-2 lg:col-span-1 lg:flex-col lg:items-start lg:justify-center lg:gap-2">
              <span className="font-mono text-[11px] tracking-[0.18em] text-white/80">{phase.n}</span>
              <div>
                <p className="text-sm font-semibold tracking-tight">{phase.title}</p>
                <p className="mt-1 hidden text-xs leading-snug text-white/75 lg:block">{phase.copy}</p>
              </div>
            </div>
            {phase.steps.map((item) => {
              const index = JOURNEY.findIndex((entry) => entry.n === item.n);
              const current = index === active;
              return (
                <button
                  key={item.n}
                  type="button"
                  role="option"
                  aria-selected={current}
                  onClick={() => go(index)}
                  onMouseEnter={() => go(index)}
                  className={`flex min-h-[6.25rem] flex-col items-start justify-between border-t border-navy/10 px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold sm:border-l lg:border-t-0 ${
                    current ? "bg-[#fff4f4]" : "bg-white hover:bg-[#faf6f4]"
                  }`}
                >
                  <span
                    className={`font-mono text-[11px] tracking-[0.16em] ${
                      current ? "text-collect-red" : "text-navy/35"
                    }`}
                  >
                    {item.n}
                  </span>
                  <span className="mt-3 text-sm font-semibold leading-snug tracking-tight text-navy">
                    {item.title}
                  </span>
                  <span
                    className={`mt-3 h-[2px] w-8 ${current ? "bg-collect-red" : "bg-transparent"}`}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        ))}
        <div className="h-1 bg-[#faf6f4]" aria-hidden="true">
          <div
            className="h-full bg-collect-gold transition-[width] duration-300"
            style={{ width: `${((active + 1) / JOURNEY.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="relative mt-6 border border-navy/10 bg-white p-6 sm:p-8">
        <CornerMarks />
        <div className="flex items-start justify-between gap-4">
          <p className="font-mono text-[11px] tracking-[0.2em] text-collect-red">
            STATION {step.n} / {String(JOURNEY.length).padStart(2, "0")}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => go(active - 1)}
              disabled={active === 0}
              className="inline-flex h-10 w-10 cursor-pointer items-center justify-center border border-navy/15 text-navy transition hover:border-collect-red hover:text-collect-red disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Previous station"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => go(active + 1)}
              disabled={active === last}
              className="inline-flex h-10 w-10 cursor-pointer items-center justify-center border border-navy/15 text-navy transition hover:border-collect-red hover:text-collect-red disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Next station"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>
        <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-tight text-navy sm:text-3xl">
          {step.title}
        </h3>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-navy/60">{step.copy}</p>
        {step.items ? (
          <ul className="mt-6 flex flex-wrap gap-2">
            {step.items.map((item) => (
              <li
                key={item}
                className="border border-navy/10 bg-[#faf6f4] px-3 py-2 text-xs font-semibold text-navy"
              >
                {item}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function CollectSystem() {
  const { fade } = useFade();

  return (
    <>
      <SectionShell id="journey" labelledBy="journey-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              How SimCollect works
            </p>
            <h2
              id="journey-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Intelligent end-to-end
              <span className="mt-1 block text-collect-red">recovery workflow</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Twelve stations in three movements — from portfolio submission to case closure — with
              a complete audit history at every handoff.
            </p>
          </motion.header>

          <JourneyBoard />

          <p className="mt-10 border-l-[3px] border-collect-gold pl-5 text-base leading-relaxed text-navy/70">
            This intelligent workflow enables organizations to recover debts efficiently while
            maintaining transparency, accountability and positive customer relationships.
          </p>
        </div>
      </SectionShell>

      <SectionShell id="industries" labelledBy="industries-heading" tone="paper">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Industries we serve
            </p>
            <h2
              id="industries-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Recovery across
              <span className="mt-1 block">every receivables book</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              SimCollect supports debt recovery across multiple sectors — from commercial banks to
              utilities and subscription businesses.
            </p>
          </motion.header>

          <ul className="mt-12 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {INDUSTRIES.map((item, index) => {
              const Icon = INDUSTRY_ICONS[item.icon] || Landmark;
              return (
                <li
                  key={item.name}
                  className="flex items-center gap-3 border-l-[3px] border-collect-red bg-white px-3 py-3"
                >
                  <span className="font-mono text-[11px] tracking-[0.14em] text-collect-gold">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <Icon className="h-4 w-4 shrink-0 text-collect-red" strokeWidth={1.75} aria-hidden="true" />
                  <span className="text-sm font-semibold leading-snug tracking-tight text-navy">
                    {item.name}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="mt-16 grid overflow-hidden border border-navy/10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
            <div className="bg-white px-6 py-8 sm:px-8 sm:py-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-collect-red">
                Technology &amp; integrations
              </p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy">
                Seamless with the systems you already run
              </h3>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-navy/60">
                SimCollect integrates with enterprise systems and payment platforms so recovery
                never lives in a silo.
              </p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {INTEGRATIONS.map((item) => (
                  <li
                    key={item}
                    className="border border-navy/10 bg-[#faf6f4] px-3 py-2 text-xs font-semibold text-navy/80"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-navy px-6 py-8 text-white sm:px-8 sm:py-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-collect-gold">
                Enterprise capabilities
              </p>
              <ul className="mt-6 space-y-3">
                {ENTERPRISE.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-snug text-white/85">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-collect-gold" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="choose" labelledBy="choose-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Why choose SimCollect?
            </p>
            <h2
              id="choose-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Many systems only send reminders.
              <span className="mt-1 block text-collect-red">SimCollect runs the full lifecycle.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Unlike conventional collection tools, SimCollect combines workflow, engagement, contact
              centre, payments, dashboards and compliance into one enterprise platform.
            </p>
          </motion.header>

          <div className="relative mt-12 border border-navy/10">
            <CornerMarks />
            <div className="grid items-stretch sm:grid-cols-2">
              <div className="flex flex-col justify-between border-b border-navy/10 bg-[#faf6f4] p-6 sm:border-b-0 sm:border-r sm:p-8">
                <p className="font-mono text-[11px] tracking-[0.2em] text-navy/40">Conventional</p>
                <h3 className="mt-6 text-xl font-semibold tracking-tight text-navy/55 sm:text-2xl">
                  Reminders, spreadsheets and a payment chase.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-navy/50">
                  Most tools stop at outbound messages. The rest of recovery still lives in disconnected
                  desks.
                </p>
              </div>
              <div className="flex flex-col justify-between bg-white p-6 sm:p-8">
                <p className="font-mono text-[11px] tracking-[0.2em] text-collect-red">SimCollect</p>
                <h3 className="mt-6 text-xl font-semibold tracking-tight text-navy sm:text-2xl">
                  One file from portfolio intake to case closure.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-navy/60">
                  Workflow, engagement, contact centre, payments, dashboards and compliance sit on the
                  same recovery lifecycle.
                </p>
              </div>
            </div>

            <ol className="grid auto-rows-fr items-stretch gap-px border-t border-navy/10 bg-navy/10 md:grid-cols-3">
              {DIFFERENTIATORS.map((item) => (
                <li key={item.n} className="flex h-full min-h-[11.5rem] flex-col bg-white p-5 sm:p-6">
                  <span className="font-mono text-[11px] tracking-[0.16em] text-collect-red">{item.n}</span>
                  <h3 className="mt-4 text-base font-semibold leading-snug tracking-tight text-navy">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-navy/60">{item.copy}</p>
                </li>
              ))}
            </ol>

            <p className="border-t border-navy/10 bg-[#faf6f4] px-6 py-5 text-base font-semibold leading-snug text-navy sm:px-8">
              The result is faster recoveries, lower costs, healthier portfolios and stronger customer
              relationships.
            </p>
          </div>
        </div>
      </SectionShell>
    </>
  );
}

export default CollectSystem;
